/**
 * Pure pricing/cost math for the schedule timeline. No React, no DOM — extracted
 * from the Schedule view so the savings claim (the product's headline number) is
 * unit-testable in isolation and reusable by the Overview hero. See scheduleCost.test.ts.
 */
import type { DaySchedule, ScheduleBlock } from "../types";

export const DAY = 1440; // minutes per day
export const ASSUMED_KW = 0.8; // flat pump draw for cost estimates; cancels in the savings ratio
export const FIXED_REF_MIN = 360; // 06:00 — the "fixed schedule" counterfactual the savings are measured against
export const SAVINGS_CAP = 0.9; // never claim more than 90% saved

const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));

/** "HH:MM" for an absolute or within-day minute. */
export const hhmm = (m: number) =>
  `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(Math.round(m % 60)).padStart(2, "0")}`;

/** kr/kWh of the price band covering an absolute minute, or null if unpriced. */
export function bandV(s: DaySchedule, minute: number): number | null {
  for (const b of s.priceBands) if (minute >= b.startMin && minute < b.startMin + b.durMin) return b.v;
  return null;
}

/** Per published day: the cheapest contiguous run(s) of power, as lane-spanning columns.
 *  Skipped when the day is too flat to tell a story. This is what drip blocks sit in. */
export function cheapColumns(s: DaySchedule): { startMin: number; durMin: number }[] {
  const out: { startMin: number; durMin: number }[] = [];
  for (let d = 0; d < s.days; d++) {
    if (!s.priceDays[d]) continue;
    const day = s.priceBands
      .filter((b) => b.startMin >= d * DAY && b.startMin < (d + 1) * DAY)
      .sort((a, b) => a.startMin - b.startMin);
    if (!day.length) continue;
    const peak = Math.max(...day.map((b) => b.v));
    const min = Math.min(...day.map((b) => b.v));
    if ((peak - min) / Math.max(0.05, peak) < 0.08) continue; // flat → no valley worth claiming
    const thresh = min + 0.02;
    let cur: { startMin: number; durMin: number } | null = null;
    for (const b of day) {
      if (b.v <= thresh) {
        if (cur && b.startMin === cur.startMin + cur.durMin) cur.durMin += b.durMin;
        else {
          if (cur) out.push(cur);
          cur = { startMin: b.startMin, durMin: b.durMin };
        }
      } else if (cur) {
        out.push(cur);
        cur = null;
      }
    }
    if (cur) out.push(cur);
  }
  return out;
}

/** Honest est. cost of a block in öre at its chosen slot (flat pump draw). null when unpriced. */
export function blockCostOre(s: DaySchedule, b: ScheduleBlock): number | null {
  let kr = 0;
  let priced = 0;
  for (let m = b.startMin; m < b.startMin + b.durMin; m++) {
    const v = bandV(s, m);
    if (v == null) continue;
    kr += (v * ASSUMED_KW) / 60;
    priced++;
  }
  return priced > 0 ? Math.round(kr * 100) : null;
}

/** What the same minutes would cost at a fixed 06:00 start that day (öre) — the
 *  counterfactual behind the savings claim. null when that slot isn't priced. */
export function fixedRefCostOre(s: DaySchedule, b: ScheduleBlock): number | null {
  const day = Math.floor(b.startMin / DAY);
  let kr = 0;
  let priced = 0;
  for (let i = 0; i < b.durMin; i++) {
    const v = bandV(s, day * DAY + FIXED_REF_MIN + i);
    if (v == null) continue;
    kr += (v * ASSUMED_KW) / 60;
    priced++;
  }
  return priced > 0 ? Math.round(kr * 100) : null;
}

/** % cheaper than running the same minutes at a fixed 06:00 start, from the price
 *  bands. Returns null when nothing shiftable is priced (so the UI shows nothing,
 *  never a fabricated number). Capped at SAVINGS_CAP. */
export function savingPct(s: DaySchedule): number | null {
  let chosen = 0;
  let fixed = 0;
  let any = false;
  for (const b of s.blocks) {
    if (b.kind === "done") continue;
    const day = Math.floor(b.startMin / DAY);
    if (!s.priceDays[day]) continue;
    for (let i = 0; i < b.durMin; i++) {
      const cv = bandV(s, b.startMin + i);
      const fv = bandV(s, day * DAY + FIXED_REF_MIN + i);
      if (cv == null || fv == null) continue;
      chosen += cv;
      fixed += fv;
      any = true;
    }
  }
  if (!any || fixed <= 0) return null;
  return Math.round(clamp(1 - chosen / fixed, 0, SAVINGS_CAP) * 100);
}
